import { useEffect, useState } from "react";
import { useAccess } from "../../lib/auth";
import { useOverlay } from "../../lib/hooks";
import { useWorld } from "../../lib/queries";
import {
  CancelSessionDialog,
  ChangeScheduleDialog,
  EditTopicDialog,
  FillRotationDialog,
  LogExtraDialog,
  MarkDoneDialog,
  NewScheduleDialog,
  ReplacementDialog,
} from "./dialogs";
import { SessionPanel } from "./SessionPanel";

const SESSION_DIALOGS = {
  "edit-topic": EditTopicDialog,
  change: ChangeScheduleDialog,
  cancel: CancelSessionDialog,
  "mark-done": MarkDoneDialog,
  replacement: ReplacementDialog,
  "log-extra": LogExtraDialog,
} as const;

type SessionDialogKey = keyof typeof SESSION_DIALOGS;

/**
 * Renders overlays driven by the URL so they deep-link and close with the back button:
 *  ?session=<id>                         → session panel (drawer / full screen)
 *  ?session=<id>&action=change|…         → dialog on top of the panel
 *  ?action=new-schedule[&date=YYYY-MM-DD] → Input schedule
 *  ?action=fill-rotation                 → Fill from rotation (System Manager)
 */
export function OverlayHost() {
  const { sessionId, action, params, closeAction } = useOverlay();
  const { world } = useWorld();
  const { show } = useAccess();
  const [lastSessionId, setLastSessionId] = useState(sessionId);

  useEffect(() => {
    if (sessionId) setLastSessionId(sessionId);
  }, [sessionId]);

  const session = sessionId ? world?.sessionsById.get(sessionId) : undefined;
  const Dialog = action && action in SESSION_DIALOGS ? SESSION_DIALOGS[action as SessionDialogKey] : undefined;
  const canEdit = show("schedule.edit");

  return (
    <>
      {lastSessionId && (
        <SessionPanel sessionId={lastSessionId} open={!!sessionId}>
          {Dialog && session && world && canEdit && (
            <Dialog key={`${action}-${session.id}`} session={session} world={world} open onClose={closeAction} />
          )}
        </SessionPanel>
      )}
      {!sessionId && action === "new-schedule" && canEdit && (
        <NewScheduleDialog key={params.get("date") ?? "new"} open onClose={closeAction} presetDate={params.get("date")} />
      )}
      {action === "fill-rotation" && show("rotation.manage") && <FillRotationDialog open onClose={closeAction} />}
    </>
  );
}
